import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { favoritoService } from '../../shared/services';
import type { Favorito } from '../../shared/types';
import { Star, BookOpen, FileText, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';

export function FavoritesPage() {
  const queryClient = useQueryClient();

  const { data: favoritos, isLoading } = useQuery({
    queryKey: ['favoritos'],
    queryFn: favoritoService.getMeusFavoritos,
  });

  const removeMutation = useMutation({
    mutationFn: ({ tipo, refId }: { tipo: string; refId: string }) =>
      favoritoService.removeFavorito(tipo, refId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favoritos'] });
    },
  });

  const getFavoritoDetalhes = (favorito: Favorito) => {
    if (favorito.tipo === 'curso') {
      return {
        titulo: favorito.titulo || 'Curso',
        descricao: favorito.descricao,
        categoria: favorito.categoria,
        url: `/cursos/${favorito.slug || favorito.refId}`,
        icon: BookOpen,
        color: 'primary',
      };
    }

    if (favorito.tipo === 'documento') {
      return {
        titulo: favorito.titulo || 'Documento',
        descricao: favorito.descricao,
        categoria: favorito.categoria,
        url: '/biblioteca',
        icon: FileText,
        color: 'red',
      };
    }

    return {
      titulo: favorito.titulo || 'Item',
      descricao: favorito.descricao,
      url: '#',
      icon: Star,
      color: 'gray',
    };
  };

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Meus Favoritos</h1>
          <p className="text-gray-600 mt-2">Conteúdos salvos para acesso rápido</p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : favoritos && favoritos.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {favoritos.map((favorito) => {
              const detalhes = getFavoritoDetalhes(favorito);
              const Icon = detalhes.icon;

              return (
                <div key={`${favorito.tipo}-${favorito.refId}`} className="card">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 bg-${detalhes.color}-100 rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`text-${detalhes.color}-600`} size={24} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <span className={`px-2 py-1 bg-${detalhes.color}-100 text-${detalhes.color}-700 text-xs font-medium rounded`}>
                            {detalhes.categoria || favorito.tipo}
                          </span>
                        </div>
                        <button
                          onClick={() => removeMutation.mutate({ tipo: favorito.tipo, refId: favorito.refId })}
                          className="text-gray-400 hover:text-red-600 transition-colors"
                          title="Remover dos favoritos"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>

                      <Link to={detalhes.url || '#'} className="block">
                        <h3 className="font-semibold text-gray-900 hover:text-primary-600 transition-colors mb-1">
                          {detalhes.titulo}
                        </h3>
                        {detalhes.descricao && (
                          <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                            {detalhes.descricao}
                          </p>
                        )}
                      </Link>

                      <p className="text-xs text-gray-500">
                        Adicionado em {new Date(favorito.createdAt).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="card text-center py-12">
            <Star className="mx-auto text-gray-400 mb-4" size={64} />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Nenhum favorito ainda
            </h3>
            <p className="text-gray-600 mb-6">
              Salve cursos e documentos para acessá-los rapidamente
            </p>
            <Link to="/catalogo" className="btn-primary inline-block">
              Explorar Catálogo
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
